import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { on } from "@ember/modifier";
import { fn } from "@ember/helper";
import { pageTitle } from "ember-page-title";
import StepPad from "#components/step-pad.gts";
import { Step } from "#models/step.ts";

const eq = <T,>(a: T, b: T): boolean => a === b;

export default class SandboxStepPadTemplateComponent extends Component {
  @tracked currentPlayheadIndex = 0;
  @tracked isPlaying = false;
  private timer: number | null = null;

  // 16 steps for interactive drum track sequence demo
  @tracked steps: Step[] = Array.from(
    { length: 16 },
    (_, i) =>
      new Step({
        velocity: i % 4 === 0 ? 1.0 : i % 2 === 0 ? 0.5 : 0,
      }),
  );

  // Single step instances for isolated state demonstration
  @tracked stepOff = new Step({ velocity: 0 });
  @tracked stepYellow = new Step({ velocity: 0.5 });
  @tracked stepRed = new Step({ velocity: 1.0 });

  cycleStep = (step: Step) => {
    step.cycleVelocity();
  };

  togglePlay = () => {
    if (this.isPlaying) {
      this.stop();
    } else {
      this.play();
    }
  };

  play = () => {
    this.isPlaying = true;
    this.timer = window.setInterval(() => {
      this.currentPlayheadIndex = (this.currentPlayheadIndex + 1) % 16;
    }, 200);
  };

  stop = () => {
    this.isPlaying = false;
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
  };

  willDestroy(): void {
    super.willDestroy();
    this.stop();
  }

  <template>
    {{pageTitle "StepPad Sandbox"}}

    <div class="space-y-12 max-w-5xl">
      {{! Section 1: Step Pad States Showcase }}
      <section class="bg-slate-900 border border-slate-800 rounded-xl p-6 shadow-md">
        <h2 class="text-lg font-semibold text-amber-400 mb-4">
          Step Pad Velocity States & LED Indicators
        </h2>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          {{! Off State }}
          <div
            class="flex flex-col items-center p-4 bg-slate-950/60 rounded-lg border border-slate-800"
          >
            <span class="text-xs font-mono text-slate-400 mb-3">Velocity: 0.0 (Unlit LED)</span>
            <StepPad @step={{this.stepOff}} @onClick={{fn this.cycleStep this.stepOff}} />
            <span class="text-[11px] text-slate-500 mt-2">Click pad to cycle</span>
          </div>

          {{! Medium Yellow State }}
          <div
            class="flex flex-col items-center p-4 bg-slate-950/60 rounded-lg border border-slate-800"
          >
            <span class="text-xs font-mono text-amber-400 mb-3">Velocity: 0.5 (Yellow LED)</span>
            <StepPad @step={{this.stepYellow}} @onClick={{fn this.cycleStep this.stepYellow}} />
            <span class="text-[11px] text-slate-500 mt-2">Click pad to cycle</span>
          </div>

          {{! Accent Red State }}
          <div
            class="flex flex-col items-center p-4 bg-slate-950/60 rounded-lg border border-slate-800"
          >
            <span class="text-xs font-mono text-red-400 mb-3">Velocity: 1.0 (Red LED)</span>
            <StepPad @step={{this.stepRed}} @onClick={{fn this.cycleStep this.stepRed}} />
            <span class="text-[11px] text-slate-500 mt-2">Click pad to cycle</span>
          </div>
        </div>
      </section>

      {{! Section 2: Active Playhead Glow Showcase }}
      <section class="bg-slate-900 border border-slate-800 rounded-xl p-6 shadow-md">
        <h2 class="text-lg font-semibold text-amber-400 mb-4">
          Active Playhead Glow States (isCurrentStep = true)
        </h2>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div
            class="flex flex-col items-center p-4 bg-slate-950/60 rounded-lg border border-slate-800"
          >
            <span class="text-xs font-mono text-slate-300 mb-3">Current Step (Off Note)</span>
            <StepPad
              @step={{this.stepOff}}
              @isCurrentStep={{true}}
              @onClick={{fn this.cycleStep this.stepOff}}
            />
          </div>

          <div
            class="flex flex-col items-center p-4 bg-slate-950/60 rounded-lg border border-slate-800"
          >
            <span class="text-xs font-mono text-amber-300 mb-3">Current Step (Yellow Glow)</span>
            <StepPad
              @step={{this.stepYellow}}
              @isCurrentStep={{true}}
              @onClick={{fn this.cycleStep this.stepYellow}}
            />
          </div>

          <div
            class="flex flex-col items-center p-4 bg-slate-950/60 rounded-lg border border-slate-800"
          >
            <span class="text-xs font-mono text-red-300 mb-3">Current Step (Red Glow)</span>
            <StepPad
              @step={{this.stepRed}}
              @isCurrentStep={{true}}
              @onClick={{fn this.cycleStep this.stepRed}}
            />
          </div>
        </div>
      </section>

      {{! Section 3: Interactive 16-Step Sequencer Bar & Playhead }}
      <section class="bg-slate-900 border border-slate-800 rounded-xl p-6 shadow-md">
        <div class="flex items-center justify-between mb-4">
          <div>
            <h2 class="text-lg font-semibold text-amber-400">
              Full 16-Step Grid & Animated Playhead
            </h2>
            <p class="text-xs text-slate-400">
              Notice beat grouping (steps 1-4, 5-8, 9-12, 13-16) and active playhead glowing LED
              behavior. Click any pad to cycle velocity.
            </p>
          </div>

          <button
            type="button"
            {{on "click" this.togglePlay}}
            class="px-4 py-2 text-xs font-bold rounded-md bg-amber-500 text-slate-950 hover:bg-amber-400 transition-colors shadow"
          >
            {{if this.isPlaying "Pause Sequencer" "Simulate Sequencer"}}
          </button>
        </div>

        <div
          class="flex flex-wrap gap-1.5 sm:gap-2 p-4 bg-slate-950 rounded-lg border border-slate-800 justify-center steps-grid"
        >
          {{#each this.steps as |step idx|}}
            <StepPad
              @step={{step}}
              @isCurrentStep={{eq idx this.currentPlayheadIndex}}
              @onClick={{fn this.cycleStep step}}
            />
          {{/each}}
        </div>
      </section>
    </div>
  </template>
}
